import { useTranslation } from 'react-i18next';
import { useBasePath } from '~/common/hooks/useBasePath';
import { useForm } from '@conform-to/react';
import { parseWithZod } from '@conform-to/zod/v4';
import { useCallback, useState } from 'react';
import { Button } from '~/common/components/ui/button';
import { FORM_FIELDS } from '~/common/constants/formfields';
import { DialogClose, DialogFooter } from '~/common/components/ui/dialog';
import ProgressButton from '~/common/components/ProgressButton';
import { cn } from '~/common/utils/styleUtil';
import { toast } from 'sonner';
import { createInventorySchema } from '~/features/inventory/schemas/createInventorySchema';
import { LimitedInput } from '~/common/components/LimitedInput';

export function InventoryCreate({
  onClose,
  isMobile,
  articles
}: Readonly<{
  onClose: () => void;
  isMobile: boolean;
  articles: { mainArticleId: number; isExternalArticle: boolean; quantity: number }[];
}>) {
  const { t } = useTranslation();
  const { path } = useBasePath();
  const [inProgress, setInProgress] = useState<boolean>(false);
  const [responseData, setResponseData] = useState<any>(null);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [form, fields] = useForm({
    lastResult: responseData,
    shouldValidate: 'onBlur',
    shouldRevalidate: 'onInput',
    onValidate({ formData }) {
      return parseWithZod(formData, { schema: createInventorySchema });
    }
  });

  const handleSubmit = useCallback(
    async event => {
      event.preventDefault();

      try {
        setInProgress(true);
        const formData = new FormData(event.target);
        formData.append(FORM_FIELDS.Intent, FORM_FIELDS.Create);
        formData.append('action', 'empty');

        const response = await fetch(`${path}/inventory/addarticletoinventory`, {
          method: 'POST',
          body: formData
        });

        if (response?.ok) {
          const data = (await response.json()) as { ok: boolean; message: string };
          setResponseData(data);
          if (data.message) {
            if (data.ok) {
              toast.success(data.message);
              onClose();
            } else {
              toast.error(data.message);
            }
          }
        }
      } finally {
        setInProgress(false);
      }
    },
    [onClose, path]
  );

  return (
    <form
      method="post"
      className={cn('flex flex-col', isMobile ? 'h-full' : 'justify-between h-full')}
      onSubmit={handleSubmit}
    >
      <input name="articles" type="hidden" defaultValue={JSON.stringify(articles)} />

      <div className="flex-1">
        <p className="my-2">{t('Bezeichnung des Inventarsatzes')}</p>
        <LimitedInput
          name={fields.name.name}
          defaultValue={fields.name.initialValue ?? ''}
          testId="inventory-create-input"
          limit={40}
          placeholder={t('Inventurname eingeben')}
        />
        <div className="mt-1 min-h-6 text-sm text-primary">{fields.name.errors?.map(e => t(e as any))}</div>
      </div>

      <DialogFooter className={'flex-col desktop:flex-row gap-2 desktop:gap-0'}>
        <>
          <DialogClose asChild>
            <Button type="button" variant="primaryOutline" onClick={onClose} data-testid="inventory-create-cancel">
              {t('Abbrechen')}
            </Button>
          </DialogClose>
          <ProgressButton
            name={FORM_FIELDS.Intent}
            value={FORM_FIELDS.Create}
            type="submit"
            data-testid="inventory-create-submit"
            inProgress={inProgress}
          >
            {t('Erstellen & hinzufügen')}
          </ProgressButton>
        </>
      </DialogFooter>
    </form>
  );
}
